import { Injectable } from '@angular/core';
import { AuthService } from '../auth.service';
import { AbstractNursecallService, ColumnTypes, Maker, NursecallInput } from './abstract-nursecall.service';
import { AlertController } from 'ionic-angular';
import { NurseCallService } from '../nurse-call.service';

type CarecomRowType = NursecallInput & {
  floorCode: string;
}

@Injectable()
export class CarecomService extends AbstractNursecallService {

  clientSystemCode: string = ""; // 送信元コード
  facilityCode: string = ""; // 病院/施設コード

  constructor(
    protected alertCtrl: AlertController,
    protected nurseCallService: NurseCallService,
    private authService: AuthService,
  ) {
    super(alertCtrl, nurseCallService);
  }

  reset() {
    this.clientSystemCode = "";
    this.facilityCode = "";
  }

  placeholderText() {
    return "ケアコム社から受け取るエクセルファイルの中身を貼り付けてください\n※エクセルファイルをbrightvieから渡す場合もあります";
  }

  columns() {
    return [
      { key: "ctlId", text: "制御ID", checkValidate: true, isShowConfirm: false },
      { key: "equipId", text: "子機ID", checkValidate: true, isShowConfirm: false },
      { key: "floorCode", text: "フロアコード", checkValidate: false, isShowConfirm: true },
      { key: "roomCode", text: "お部屋コード", checkValidate: true, isShowConfirm: true },
      { key: "roomName", text: "お部屋名", checkValidate: false, isShowConfirm: true },
      { key: "bedNo", text: "ベッド番号", checkValidate: true, isShowConfirm: true },
    ];
  }

  /**
   * ケアコムの入力データをパース
   *  - 下記エクセルがサンプル。エクセル全体をコピーして貼り付けられた結果がinputTextに来る
   *    https://docs.google.com/spreadsheets/d/1e6qL6IhBc85bcpFSC9ejPrhmbrRbI2cz/edit?gid=164765101#gid=164765101
   */
  parse({ inputText }: { inputText: string }) {
    const rows: CarecomRowType[] = [];
    const _rows = inputText.split('\n');

    // 送信元コード取得
    this.clientSystemCode = _rows[1].split('\t')[8];
    // 病院／施設コード取得
    this.facilityCode = _rows[2].split('\t')[8];

    const headerRows = 2; // 読み込まないヘッダー行
    let currentRow = 1;
    for (const row of _rows) {
      if (currentRow < headerRows) {
        currentRow++;
        continue;
      }
      const columns = row.split('\t');
      rows.push({
        // 制御ID: 送信元コード_施設コード
        ctlId: `${this.clientSystemCode}_${this.facilityCode}`,
        // 子機ID: 事業所ID_フロアコード_部屋コード_ベッド番号
        equipId: `${this.authService.user.contractId}_${columns[1]}_${columns[2]}_${columns[3]}`,
        callNoCd: '', // 固定値
        floorCode: columns[1], // フロアコード
        roomCode: columns[2], // 部屋名
        roomName: `${columns[4]} ${columns[5]}`, // 部屋コード
        bedNo: columns[3], // ベッド番号
        memo: '', // 固定値
      });
    }
    return rows;
  }

  async validate({ maker, rows }: { maker: Maker, rows: CarecomRowType[]; }) {
    // チェック対象のカラムを取得
    const targetColumns = this.columns().filter((column) => column.checkValidate);
    if (!this.validateRows(rows, targetColumns)) {
      return false;
    }
    if (!this.validateDuplicated(rows, targetColumns)) {
      return false;
    }
    if (!await this.validateRegistered(maker, rows, targetColumns)) {
      return false;
    }
    return true;
  }


  /**
   * rowsのうち重複しているtargetColumnsのリストを取得する
   */
  getDuplicatedIds(rows: NursecallInput[], targetColumns: ColumnTypes[] = []): string[] {

    const checkedObj = {};
    const duplicatedObj = {};

    // 子機IDの重複チェック
    const targetColumn = targetColumns.find((column) => column.key === "equipId");

    for (const row of rows) {
      // targetColumnsの値をキーとして重複をチェック
      const key = row[targetColumn.key];
      if (!key) {
        continue;
      }
      if (!checkedObj[targetColumn.key]) {
        checkedObj[targetColumn.key] = {};
      }
      if (!duplicatedObj[targetColumn.key]) {
        duplicatedObj[targetColumn.key] = {};
      }
      if (checkedObj[targetColumn.key][key]) {
        duplicatedObj[targetColumn.key][key] = true;
      } else {
        checkedObj[targetColumn.key][key] = true;
      }
    }

    const duplicatedText = [];
    Object.keys(duplicatedObj[targetColumn.key]).forEach((value) => {
      // 子機IDをフォーム内容に分解して表示する
      const [contractId, floorCode, roomCode, bedNo] = value.split('_');
      duplicatedText.push(`フロアコードと部屋コードとベッド番号の組み合わせが重複しています: <br>・フロアコード: ${floorCode}<br>・部屋コード: ${roomCode}<br>・ベッド番号: ${bedNo}<br>`);
    });

    return duplicatedText;

  }

  /**
   * 入力されたデータのうちすでに登録済みのリストを取得する
   */
  async getRegisteredIds(maker: Maker, rows: NursecallInput[], targetColumns: ColumnTypes[] = []): Promise<string[]> {

    if (!maker) return [];

    const { makerCode, productCode } = maker;

    // 同じメーカー、同じ製品のシリアルIDのリストを取得
    const res = await this.nurseCallService.loadAsPromise();
    const registeredNursecallList = res.data.callRoomList
      .filter(
        (nursecall) =>
          nursecall.makerCode === makerCode && nursecall.productCode === productCode
      )

    const duplicatedObj = {};

    // 子機IDの重複チェック
    const targetColumnCtlId = targetColumns.find((column) => column.key === "ctlId");
    const targetColumnEquipId = targetColumns.find((column) => column.key === "equipId");

    for (const row of rows) {
      const dup = registeredNursecallList.find((nursecall) => {
        return (targetColumnCtlId.key in nursecall && nursecall[targetColumnCtlId.key] === row[targetColumnCtlId.key]) && (targetColumnEquipId.key in nursecall && nursecall[targetColumnEquipId.key] === row[targetColumnEquipId.key]);
      });

      if (dup) {
        if (!duplicatedObj[dup._id]) {
          duplicatedObj[dup._id] = {};
        }
        duplicatedObj[dup._id][targetColumnCtlId.key] = row[targetColumnCtlId.key];
        duplicatedObj[dup._id][targetColumnEquipId.key] = row[targetColumnEquipId.key];
      }
    }

    const duplicatedText = [];
    Object.keys(duplicatedObj).forEach((id) => {

      const valueCtlId = duplicatedObj[id][targetColumnCtlId.key];
      const valueEquipId = duplicatedObj[id][targetColumnEquipId.key];

      if (!valueCtlId && !valueEquipId) {
        return;
      }

      let text = "以下の組み合わせは既に登録されています: <br>";
      if (valueCtlId) {
        // 制御IDを表示用に変換
        const [clientSystemCode, facilityCode] = valueCtlId.split('_');
        text += `・送信元コード: ${clientSystemCode}<br>・病院／施設コード: ${facilityCode}<br>`;
      }
      if (valueEquipId) {
        // 子機IDを表示用に変換
        const [contractId, floorCode, roomCode, bedNo] = valueEquipId.split('_');
        text += `・フロアコード: ${floorCode}<br>・部屋コード: ${roomCode}<br>・ベッド番号: ${bedNo}<br>`;
      }
      duplicatedText.push(text);

    });

    return duplicatedText;
  }

}



// WEBPACK FOOTER //
// ./src/providers/nursecall/carecom.service.ts